import type { ReactNode } from "react";
import { SAUNA_QUOTE, type SaunaSection } from "../data/sauna-seo";

const EMBER = "hsl(18 82% 52%)";
const EMBER_SOFT = "hsl(22 90% 66%)";
const NAVY = "hsl(222 47% 11%)";

export const SERVICE_CARDS: Record<string, { image: string; alt: string; blurb: string }> = {
  "/sauna/install/home-sauna-installation": { image: "sauna-detail-1.webp", alt: "Timber sauna interior with bench seating", blurb: "A traditional sauna in a suitable spare room, wellness room or refurbishment, specified around the space and your household." },
  "/sauna/install/outdoor-sauna-installation": { image: "sauna-assembly.webp", alt: "Timber sauna cabin being assembled", blurb: "Garden cabins and outdoor buildings, coordinated with the base, delivery route, weather protection and electrical supply." },
  "/sauna/install/commercial-sauna-installation": { image: "sauna-fitting.webp", alt: "Engineer fitting a timber sauna entrance", blurb: "New and replacement facilities for gyms, hotels, spas and wellness operators, planned around usage and maintenance access." },
};

const PHOTOS: { file: string; alt: string }[] = [
  { file: "sauna-heater.webp", alt: "Sauna heater with stones installed in a timber cabin" },
  { file: "sauna-panel-assembly.webp", alt: "Sauna wall panels being assembled on site" },
  { file: "sauna-install.webp", alt: "Sauna installation in progress" },
  { file: "sauna-detail-2.webp", alt: "Timber sauna detail" },
  { file: "sauna-detail-4.webp", alt: "Sauna bench and lining detail" },
  { file: "sauna-detail-3.webp", alt: "Sauna interior detail" },
  { file: "sauna-design.webp", alt: "Sauna design detail" },
];

const isExternal = (href: string) => /^https?:\/\//.test(href);
const join = (base: string, href: string) => (isExternal(href) ? href : `${base.replace(/\/+$/, "")}${href}`);
const img = (base: string, file: string) => join(base, `/images/${file}`);
const pad = (n: number) => String(n).padStart(2, "0");
const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(18_82%_52%)] focus-visible:ring-offset-2";

function splitLead(text: string) {
  const i = text.indexOf(": ");
  if (i > 0 && i < 60) return { lead: text.slice(0, i + 1), rest: text.slice(i + 1) };
  return { lead: "", rest: text };
}

function Arrow() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Check() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Eyebrow({ children, dark = false }: { children: ReactNode; dark?: boolean }) {
  return <p className="font-mono text-xs uppercase tracking-[0.2em] mb-3" style={{ color: dark ? EMBER_SOFT : EMBER }}>{children}</p>;
}

function Links({ links, base, dark = false }: { links?: SaunaSection["links"]; base: string; dark?: boolean }) {
  if (!links || links.length === 0) return null;
  return (
    <ul className="mt-5 flex flex-wrap gap-2">
      {links.map((l) => {
        const ext = isExternal(l.href);
        const primary = l.href.startsWith(SAUNA_QUOTE.split("?")[0]);
        return (
          <li key={l.href + l.label}>
            <a
              href={join(base, l.href)}
              {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className={`group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${focusRing} ${
                primary ? "text-white hover:brightness-110" : dark ? "border border-white/25 text-white hover:bg-white/10" : "border border-border bg-card text-foreground hover:border-[hsl(18_82%_52%)]"
              }`}
              style={primary ? { background: EMBER } : undefined}
            >
              <span>{l.label}</span>
              {ext && <span className="text-xs font-normal opacity-70">(external)</span>}
              <span className="transition-transform group-hover:translate-x-0.5"><Arrow /></span>
            </a>
          </li>
        );
      })}
    </ul>
  );
}

function Paras({ items, className = "" }: { items: string[]; className?: string }) {
  return <>{items.map((p, i) => <p key={i} className={`leading-relaxed mb-3 last:mb-0 ${className}`}>{p}</p>)}</>;
}

function CheckList({ items, dark = false, cols = false }: { items: string[]; dark?: boolean; cols?: boolean }) {
  return (
    <ul className={`mt-5 grid gap-2.5 ${cols ? "sm:grid-cols-2" : ""}`}>
      {items.map((b, i) => (
        <li key={i} className={`flex gap-3 rounded-xl p-3 text-[0.95rem] leading-snug ${dark ? "bg-white/5 text-white/85" : "bg-secondary/60 text-foreground"}`}>
          <span className="mt-0.5 grid place-items-center w-5 h-5 rounded-full shrink-0 text-white" style={{ background: EMBER }}><Check /></span>
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

type Block =
  | { t: "cards"; s: SaunaSection; n: number }
  | { t: "steps"; s: SaunaSection; n: number; items: { num: string; lead: string; rest: string }[] }
  | { t: "related"; s: SaunaSection; n: number }
  | { t: "group"; items: { s: SaunaSection; n: number }[] };

function classify(sections: SaunaSection[]): Block[] {
  const blocks: Block[] = [];
  sections.forEach((s, i) => {
    const n = i + 1;
    const bullets = s.bullets ?? [];
    if (s.links && s.links.length > 0 && s.links.every((l) => SERVICE_CARDS[l.href])) return void blocks.push({ t: "cards", s, n });
    if (bullets.length >= 3 && bullets.every((b) => /^\d+\./.test(b))) {
      const items = bullets.map((b) => {
        const m = b.match(/^(\d+)\.\s*(.*?)\s+—\s+(.*)$/);
        return m ? { num: m[1], lead: m[2], rest: m[3] } : { num: "", lead: "", rest: b };
      });
      return void blocks.push({ t: "steps", s, n, items });
    }
    if (bullets.length === 0 && s.paragraphs.length >= 3 && s.paragraphs.every((p) => splitLead(p).lead) && /how it works/i.test(s.heading)) {
      const items = s.paragraphs.map((p) => ({ num: "", ...splitLead(p) }));
      return void blocks.push({ t: "steps", s, n, items });
    }
    if (s.paragraphs.length === 0 && bullets.length === 0 && s.links && s.links.length > 0) return void blocks.push({ t: "related", s, n });
    const last = blocks[blocks.length - 1];
    if (last && last.t === "group") last.items.push({ s, n });
    else blocks.push({ t: "group", items: [{ s, n }] });
  });
  return blocks;
}

function Feature({ s, n, base, photo }: { s: SaunaSection; n: number; base: string; photo: { file: string; alt: string } }) {
  const [first, ...rest] = s.paragraphs;
  return (
    <div className="grid lg:grid-cols-12 gap-4">
      <section className="lg:col-span-7 rounded-3xl p-7 md:p-9 text-white relative overflow-hidden" style={{ background: NAVY }}>
        <div aria-hidden className="absolute -right-24 -top-24 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: EMBER }} />
        <div className="relative">
          <Eyebrow dark>{pad(n)} / Start here</Eyebrow>
          <h2 className="font-display font-bold text-2xl md:text-4xl leading-tight mb-5">{s.heading}</h2>
          {first && <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-4">{first}</p>}
          <Paras items={rest} className="text-white/70" />
          {s.bullets && <CheckList items={s.bullets} dark />}
          <Links links={s.links} base={base} dark />
        </div>
      </section>
      <figure className="lg:col-span-5 relative rounded-3xl overflow-hidden min-h-[260px]">
        <img src={img(base, photo.file)} alt={photo.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[hsl(222_47%_11%/0.65)] via-transparent to-transparent" />
        <figcaption className="absolute bottom-5 left-6 right-6 flex items-center gap-3 text-white">
          <span className="h-px w-8" style={{ background: EMBER_SOFT }} />
          <span className="font-mono text-xs uppercase tracking-[0.2em]">Vertex Climate sauna work</span>
        </figcaption>
      </figure>
    </div>
  );
}

function BentoCard({ s, n, base, photo, tone }: { s: SaunaSection; n: number; base: string; photo?: { file: string; alt: string }; tone: "card" | "tint" }) {
  const heavyList = (s.bullets?.length ?? 0) >= 4;
  return (
    <section className={`h-full rounded-3xl border border-border overflow-hidden flex flex-col ${tone === "tint" ? "bg-secondary/50" : "bg-card"}`}>
      {photo && <img src={img(base, photo.file)} alt={photo.alt} loading="lazy" className="w-full aspect-[16/7] object-cover" />}
      <div className="p-6 md:p-8 flex-1">
        <div className="flex items-center gap-3 mb-3">
          <span className="font-mono text-xs px-2 py-1 rounded-md text-white" style={{ background: EMBER }}>{pad(n)}</span>
          <span className="h-px flex-1 bg-border" />
        </div>
        <h2 className="font-display font-bold text-xl md:text-2xl leading-tight mb-3">{s.heading}</h2>
        <div className="text-muted-foreground text-[0.98rem]"><Paras items={s.paragraphs} /></div>
        {s.bullets && <CheckList items={s.bullets} cols={heavyList} />}
        <Links links={s.links} base={base} />
      </div>
    </section>
  );
}

function ZigRow({ s, n, base, photo, flip }: { s: SaunaSection; n: number; base: string; photo: { file: string; alt: string }; flip: boolean }) {
  return (
    <section className="rounded-3xl border border-border bg-card overflow-hidden grid md:grid-cols-12">
      <figure className={`relative md:col-span-4 h-48 sm:h-56 md:h-auto md:min-h-[220px] ${flip ? "md:order-2" : ""}`}>
        <img src={img(base, photo.file)} alt={photo.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      </figure>
      <div className={`md:col-span-8 p-6 md:p-8 ${flip ? "md:order-1" : ""}`}>
        <div className="flex items-center gap-3 mb-3">
          <span className="font-mono text-xs px-2 py-1 rounded-md text-white" style={{ background: EMBER }}>{pad(n)}</span>
          <span className="h-px flex-1 bg-border" />
        </div>
        <h2 className="font-display font-bold text-xl md:text-2xl leading-tight mb-3">{s.heading}</h2>
        <div className="text-muted-foreground text-[0.98rem]"><Paras items={s.paragraphs} /></div>
        {s.bullets && <CheckList items={s.bullets} cols={s.bullets.length >= 4} />}
        <Links links={s.links} base={base} />
      </div>
    </section>
  );
}

function Group({ items, base, photos, isFirst, zigStart }: { items: { s: SaunaSection; n: number }[]; base: string; photos: () => { file: string; alt: string }; isFirst: boolean; zigStart: (c: number) => number }) {
  let list = items;
  let feature: ReactNode = null;
  if (isFirst && list.length > 0) {
    feature = <Feature s={list[0].s} n={list[0].n} base={base} photo={photos()} />;
    list = list.slice(1);
  }
  const start = list.length > 0 ? zigStart(list.length) : -1;
  return (
    <div className="container mx-auto px-4 max-w-7xl py-8 lg:py-12">
      {feature}
      {list.length > 0 && (
        <div {...(start === 0 ? { id: "sauna-service-details" } : {})} className={`grid gap-4 scroll-mt-28 ${feature ? "mt-4" : ""}`}>
          {list.map(({ s, n }, j) => <ZigRow key={s.heading} s={s} n={n} base={base} photo={photos()} flip={(start + j) % 2 === 1} />)}
        </div>
      )}
    </div>
  );
}

function Steps({ s, n, base, items }: { s: SaunaSection; n: number; base: string; items: { num: string; lead: string; rest: string }[] }) {
  const isParas = !(s.bullets && s.bullets.length > 0 && items.length === s.bullets.length);
  return (
    <section className="text-white py-14 lg:py-16 relative overflow-hidden" style={{ background: NAVY }}>
      <div aria-hidden className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 64px)" }} />
      <div className="container relative mx-auto px-4 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-6 mb-8 items-end">
          <div className="lg:col-span-6">
            <Eyebrow dark>{pad(n)} / Process</Eyebrow>
            <h2 className="font-display font-bold text-2xl md:text-4xl leading-tight">{s.heading}</h2>
          </div>
          {!isParas && s.paragraphs.length > 0 && <div className="lg:col-span-6 text-white/70"><Paras items={s.paragraphs} /></div>}
        </div>
        <ol className="grid gap-3 sm:grid-cols-2 lg:grid-flow-col lg:auto-cols-fr lg:grid-cols-none">
          {items.map((st, i) => (
            <li key={i} className="relative rounded-2xl bg-white/[0.04] border border-white/10 p-5">
              <span className="font-mono text-2xl font-bold" style={{ color: i === 0 ? EMBER : "hsl(0 0% 100% / 0.35)" }}>{st.num ? `${st.num}.` : pad(i + 1)}</span>
              <p className="mt-2 text-sm leading-relaxed text-white/70">
                {st.lead && <strong className="block font-display text-base text-white mb-1">{st.lead}</strong>}
                {!isParas && st.lead && <span className="sr-only"> — </span>}
                {st.rest}
              </p>
            </li>
          ))}
        </ol>
        <Links links={s.links} base={base} dark />
      </div>
    </section>
  );
}

function Cards({ s, n, base }: { s: SaunaSection; n: number; base: string }) {
  const links = s.links ?? [];
  return (
    <section className="bg-secondary/40 border-y border-border py-12 lg:py-16">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-6 mb-8 items-end">
          <div className="lg:col-span-5">
            <Eyebrow>{pad(n)} / Services</Eyebrow>
            <h2 className="font-display font-bold text-2xl md:text-4xl leading-tight">{s.heading}</h2>
          </div>
          <div className="lg:col-span-7 text-muted-foreground"><Paras items={s.paragraphs} /></div>
        </div>
        <ul className="grid gap-4 md:grid-cols-6">
          {links.map((l, i) => {
            const c = SERVICE_CARDS[l.href];
            const big = i === 0;
            return (
              <li key={l.href} className={big ? "md:col-span-6 lg:col-span-3 lg:row-span-2" : "md:col-span-3"}>
                <a href={join(base, l.href)} className={`group relative block h-full min-h-[240px] rounded-3xl overflow-hidden text-white ${focusRing}`}>
                  <img src={img(base, c.image)} alt={c.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                  <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[hsl(222_47%_11%/0.92)] via-[hsl(222_47%_11%/0.45)] to-transparent" />
                  <div className={`relative h-full flex flex-col justify-end p-6 ${big ? "lg:p-8 lg:min-h-[480px]" : ""}`}>
                    <span className="font-mono text-xs" style={{ color: EMBER_SOFT }}>{pad(i + 1)}</span>
                    <h3 className={`font-display font-bold leading-tight mt-1 mb-2 ${big ? "text-2xl md:text-3xl" : "text-xl"}`}>{l.label}</h3>
                    <p className="text-white/75 text-sm leading-relaxed mb-4 max-w-md">{c.blurb}</p>
                    <span className="inline-flex items-center gap-2 font-semibold text-sm" style={{ color: EMBER_SOFT }}>View service <span className="transition-transform group-hover:translate-x-1"><Arrow /></span></span>
                  </div>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function Related({ s, n, base }: { s: SaunaSection; n: number; base: string }) {
  return (
    <section className="container mx-auto px-4 max-w-7xl py-10 lg:py-14">
      <div className="rounded-3xl border border-border bg-card p-6 md:p-8 grid lg:grid-cols-12 gap-6 items-center">
        <div className="lg:col-span-4">
          <Eyebrow>{pad(n)} / Explore</Eyebrow>
          <h2 className="font-display font-bold text-2xl leading-tight">{s.heading}</h2>
        </div>
        <ul className="lg:col-span-8 grid gap-3 sm:grid-cols-2">
          {(s.links ?? []).map((l) => (
            <li key={l.href + l.label}>
              <a href={join(base, l.href)} {...(isExternal(l.href) ? { target: "_blank", rel: "noopener noreferrer" } : {})} className={`group flex items-center justify-between gap-3 rounded-2xl border border-border bg-secondary/40 px-5 py-4 font-semibold hover:border-[hsl(18_82%_52%)] transition-colors ${focusRing}`}>
                <span>{l.label}{isExternal(l.href) && <span className="ml-1 text-xs font-normal opacity-70">(external)</span>}</span>
                <span className="transition-transform group-hover:translate-x-1" style={{ color: EMBER }}><Arrow /></span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function SaunaServiceContent({ sections, base, image }: { sections: SaunaSection[]; base: string; image: string }) {
  const pool = PHOTOS.filter((p) => p.file !== image);
  let k = 0;
  const photos = () => pool[k++ % pool.length];
  const blocks = classify(sections);
  let firstGroup = true;
  let zc = 0;
  const zigStart = (c: number) => { const v = zc; zc += c; return v; };
  return (
    <div id="sauna-service-content" className="scroll-mt-28 bg-background">
      {blocks.map((b, i) => {
        if (b.t === "cards") return <Cards key={i} s={b.s} n={b.n} base={base} />;
        if (b.t === "steps") return <Steps key={i} s={b.s} n={b.n} base={base} items={b.items} />;
        if (b.t === "related") return <Related key={i} s={b.s} n={b.n} base={base} />;
        const isFirst = firstGroup;
        firstGroup = false;
        return <Group key={i} items={b.items} base={base} photos={photos} isFirst={isFirst} zigStart={zigStart} />;
      })}
    </div>
  );
}
